/* eslint-disable jsx-a11y/anchor-is-valid */
import { useRef, useState } from "react";
import {
    Alert,
    Avatar,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    List,
    Modal,
    Row,
    Tabs,
    Tag,
} from "antd";
import { CalendarOutlined, CloseCircleOutlined, EditOutlined, EnvironmentOutlined, ExclamationCircleOutlined, EyeOutlined, FileTextOutlined, IdcardOutlined, MailOutlined, PhoneOutlined, PlusOutlined, PrinterOutlined, SafetyOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import { toast } from "react-toastify";

import { frenchDate, frenchDateOnly } from "../../../../../variables/functions";
import AjouterExpertise from "../../../components/AjouterExpertise";
import AssignExpertsModal from "../../../components/AssignExpertsModal";
import { displayNumbers } from "../../../../../variables/numbersFunctions";
import NoteExpert from "../../../docs/NoteExpert";
import NoteVictime from "../../../docs/NoteVictime";
import { useReactToPrint } from "react-to-print";

const ExpertsTab = ({ sinistre, experts, expertises, setRefetch, editMode, setEditMode }) => {
    const [isExpertiseVisible, setIsExpertiseVisible] = useState(false);
    const [isAssignExpertVisible, setIsAssignExpertVisible] = useState(false);
    const [currentExpertise, setCurrentExpertise] = useState(null);
    const [letterModal, setLetterModal] = useState(false);
    const [victimLetterModal, setVictimLetterModal] = useState(false);
    const [cancelLoadingId, setCancelLoadingId] = useState(null);
    const printRef = useRef(null);
    const victimPrintRef = useRef(null);

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        pageStyle: `
            @page {
                size: A4 portrait !important;
                margin: 20mm;
            }
            @media print {
                html, body {
                    width: 210mm;
                    height: 297mm;
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `,
        documentTitle: 'Note expert'
    });

    const handleVictimPrint = useReactToPrint({
        content: () => victimPrintRef.current,
        pageStyle: `
            @page {
                size: A4 portrait !important;
                margin: 20mm;
            }
            @media print {
                html, body {
                    width: 210mm;
                    height: 297mm;
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
            }
        `,
        documentTitle: 'Note victime'
    });

    const handleOpenNewExpertise = () => {
        setEditMode(false);
        setCurrentExpertise(null);
        setIsExpertiseVisible(true);
    };

    const handleEditExpertise = (item) => {
        setEditMode(true);
        setCurrentExpertise(item);
        setIsExpertiseVisible(true);
    };

    const handleOpenAssignExpert = () => {
        setIsAssignExpertVisible(true);
    };

    const handleOpenLetterModal = (item) => {
        setLetterModal(true);
        setCurrentExpertise(item);
    };

    const handleOpenVictimLetterModal = (item) => {
        setVictimLetterModal(true);
        setCurrentExpertise(item);
    };

    const getExpertName = (expert) => {
        const nom = expert?.nom || "";
        const prenom = expert?.prenom || "";
        return prenom ? `${nom} ${prenom}` : nom || "cet expert";
    };

    const cancelExpert = async (expert) => {
        if (!sinistre?.codeId || !expert?.codeId) {
            toast.error("Impossible d'identifier le sinistre ou l'expert");
            return;
        }
        setCancelLoadingId(expert.codeId);
        try {
            await axiosInstance.delete(`/api/sinistre/expert/codeId/${sinistre.codeId}/${expert.codeId}`);
            toast.success("Expert annulé avec succès");
            setRefetch && setRefetch((prev) => !prev);
        } catch (err) {
            console.error(err);
            if (err?.response?.status === 404) {
                toast.error("Aucun expert associé ou expert introuvable pour ce sinistre");
            } else {
                toast.error("Échec lors de l'annulation de l'expert");
            }
        } finally {
            setCancelLoadingId(null);
        }
    };

    const confirmCancelExpert = (expert) => {
        Modal.confirm({
            title: `Voulez-vous Retirer l'expert ${getExpertName(expert)} ?`,
            icon: <ExclamationCircleOutlined />,
            okText: "Oui, Retirer",
            cancelText: "Non",
            okButtonProps: { danger: true },
            onOk: () => cancelExpert(expert),
        });
    };

    const expertiseTabContent = (
        <div style={{ padding: '24px' }}>
            {experts.length ? (
                <div style={{ marginBottom: '24px' }}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={handleOpenNewExpertise}
                        disabled={sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T"}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            height: '40px',
                            padding: '0 24px',
                            fontSize: '15px',
                            fontWeight: '600',
                            borderRadius: '10px',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                        }}
                    >
                        Nouvelle Expertise
                    </Button>
                </div>
            ) : (
                <Alert
                    message="Aucun expert associé à ce dossier"
                    description="Veuillez d'abord affecter des experts dans l'onglet 'Experts' avant de créer une expertise."
                    type="info"
                    showIcon
                    icon={<SafetyOutlined />}
                    style={{
                        marginBottom: '24px',
                        borderRadius: '12px',
                        border: '1px solid #91d5ff',
                        background: 'linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)'
                    }}
                />
            )}

            {expertises.length ? (
                <Row gutter={[24, 24]}>
                    {expertises.map((item, index) => (
                        <Col xs={24} lg={12} key={item.evaluationId || index}>
                            <Card
                                style={{
                                    height: '100%',
                                    borderRadius: '16px',
                                    border: 'none',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                }}
                                styles={{ body: { padding: 0 } }}
                            >
                                {/* Barre de couleur selon le statut */}
                                <div style={{
                                    height: '6px',
                                    background: '#242166'
                                }} />

                                <div style={{ padding: '24px' }}>
                                    {/* En-tête */}
                                    <div style={{ marginBottom: '20px' }}>
                                        <Row justify="space-between" align="top">
                                            <Col span={18}>
                                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                                    <Avatar
                                                        size={64}
                                                        style={{
                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                            fontSize: '24px',
                                                            fontWeight: 'bold',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </Avatar>
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <h3 style={{
                                                            fontSize: '18px',
                                                            fontWeight: '700',
                                                            color: '#1e293b',
                                                            margin: '0 0 6px 0',
                                                            lineHeight: '1.3'
                                                        }}>
                                                            Expertise N° {index + 1}
                                                        </h3>
                                                        <Tag
                                                            icon={<IdcardOutlined />}
                                                            style={{
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                padding: '2px 8px',
                                                                borderRadius: '6px',
                                                                border: 'none',
                                                                background: '#f1f5f9',
                                                                color: '#475569'
                                                            }}
                                                        >
                                                            {item.codeId}
                                                        </Tag>
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>

                                    {/* Informations principales */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '10px'
                                        }}>
                                            <div style={{
                                                width: '4px',
                                                height: '16px',
                                                borderRadius: '2px',
                                                background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)'
                                            }} />
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#64748b',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Bénéficiaire
                                            </span>
                                        </div>
                                        <div style={{
                                            background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                                            borderRadius: '12px',
                                            padding: '14px',
                                            border: '2px solid #6366f1'
                                        }}>
                                            <div style={{
                                                fontSize: '16px',
                                                fontWeight: '700',
                                                color: '#312e81',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <UserOutlined style={{ fontSize: '18px' }} />
                                                {item?.beneficiaire?.personne?.prenom
                                                    ? `${item?.beneficiaire?.personne?.nom} ${item?.beneficiaire?.personne?.prenom}`
                                                    : item?.beneficiaire?.personne?.nom || "-"}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Expert */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '10px'
                                        }}>
                                            <div style={{
                                                width: '4px',
                                                height: '16px',
                                                borderRadius: '2px',
                                                background: 'linear-gradient(180deg, #f59e0b 0%, #d97706 100%)'
                                            }} />
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#64748b',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Expert Désigné
                                            </span>
                                        </div>
                                        <div style={{
                                            background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                            borderRadius: '12px',
                                            padding: '14px',
                                            border: '2px solid #f59e0b'
                                        }}>
                                            <div style={{
                                                fontSize: '15px',
                                                fontWeight: '700',
                                                color: '#92400e',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}>
                                                <SafetyOutlined style={{ fontSize: '18px' }} />
                                                {item?.expert?.nom || "-"} {item?.expert?.prenom || ""}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Objet de la mission */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '10px'
                                        }}>
                                            <div style={{
                                                width: '4px',
                                                height: '16px',
                                                borderRadius: '2px',
                                                background: 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)'
                                            }} />
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#64748b',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Objet de la Mission
                                            </span>
                                        </div>
                                        <div style={{
                                            background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)',
                                            borderRadius: '12px',
                                            padding: '14px',
                                            border: '2px solid #3b82f6'
                                        }}>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '600',
                                                color: '#1e40af',
                                                lineHeight: '1.6',
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '8px'
                                            }}>
                                                <FileTextOutlined style={{ fontSize: '16px', marginTop: '2px', flexShrink: 0 }} />
                                                <span>{item?.objetMission || "-"}</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Dates */}
                                    <div style={{
                                        borderTop: '1px dashed #e2e8f0',
                                        paddingTop: '16px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                    }}>
                                        {item.dateExpertise && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontSize: '13px',
                                                color: '#64748b'
                                            }}>
                                                <CalendarOutlined style={{ color: '#3b82f6' }} />
                                                <span style={{ fontWeight: '500' }}>Date expertise:</span>
                                                <span style={{ fontWeight: '600', color: '#1e293b' }}>
                                                    {frenchDateOnly(item.dateExpertise)}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            fontSize: '12px',
                                            color: '#94a3b8'
                                        }}>
                                            <CalendarOutlined />
                                            <span>Enregistré le {frenchDate(item?.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Actions en bas */}
                                <div style={{
                                    borderTop: '1px solid #e2e8f0',
                                    padding: '16px 24px',
                                    background: '#f8fafc',
                                    display: 'flex',
                                    gap: '8px'
                                }}>
                                    <Button
                                        type="text"
                                        icon={<EditOutlined style={{ fontSize: '16px' }} />}
                                        onClick={() => handleEditExpertise(item)}
                                        disabled={sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T"}
                                        style={{
                                            flex: 1,
                                            fontWeight: '600',
                                            color: '#3b82f6',
                                            height: '40px',
                                            borderRadius: '8px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#eff6ff';
                                            e.currentTarget.style.color = '#1e40af';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = '#3b82f6';
                                        }}
                                    >
                                        Modifier
                                    </Button>
                                    <Button
                                        icon={<EyeOutlined style={{ fontSize: '16px' }} />}
                                        onClick={() => handleOpenLetterModal(item)}
                                        style={{
                                            flex: 1,
                                            fontWeight: '600',
                                            color: '#10b981',
                                            height: '40px',
                                            borderRadius: '8px',
                                            transition: 'all 0.2s',
                                            border: '1px solid #d1fae5'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#d1fae5';
                                            e.currentTarget.style.color = '#047857';
                                            e.currentTarget.style.borderColor = '#10b981';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = '#10b981';
                                            e.currentTarget.style.borderColor = '#d1fae5';
                                        }}
                                    >
                                        Note Expert
                                    </Button>
                                    <Button
                                        icon={<UserOutlined style={{ fontSize: '16px' }} />}
                                        onClick={() => handleOpenVictimLetterModal(item)}
                                        style={{
                                            flex: 1,
                                            fontWeight: '600',
                                            color: '#8b5cf6',
                                            height: '40px',
                                            borderRadius: '8px',
                                            transition: 'all 0.2s',
                                            border: '1px solid #ede9fe'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#ede9fe';
                                            e.currentTarget.style.color = '#6d28d9';
                                            e.currentTarget.style.borderColor = '#8b5cf6';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.color = '#8b5cf6';
                                            e.currentTarget.style.borderColor = '#ede9fe';
                                        }}
                                    >
                                        Note Victime
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <Card style={{
                    borderRadius: '16px',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    padding: '40px'
                }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <div>
                                <p style={{
                                    fontSize: '16px',
                                    color: '#64748b',
                                    fontWeight: '500',
                                    margin: '16px 0 8px 0'
                                }}>
                                    Aucune expertise enregistrée
                                </p>
                                <p style={{
                                    fontSize: '14px',
                                    color: '#94a3b8',
                                    margin: 0
                                }}>
                                    Cliquez sur le bouton ci-dessus pour créer une nouvelle expertise
                                </p>
                            </div>
                        }
                    />
                </Card>
            )}
        </div>
    );


    const expertsTabContent = (
        <div style={{ padding: '24px' }}>
            <div style={{ marginBottom: '24px' }}>
                <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={handleOpenAssignExpert}
                    disabled={sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T"}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        border: 'none',
                        height: '40px',
                        padding: '0 24px',
                        fontSize: '15px',
                        fontWeight: '600',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                    }}
                >
                    Affecter des Experts
                </Button>
            </div>

            {experts.length ? (
                <Row gutter={[24, 24]}>
                    {experts.map((item, index) => (
                        <Col xs={24} sm={24} md={12} lg={8} key={index}>
                            <Card
                                style={{
                                    height: '100%',
                                    borderRadius: '16px',
                                    border: 'none',
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    overflow: 'hidden'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-8px)';
                                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(0,0,0,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                }}
                                styles={{ body: { padding: 0 } }}
                            >
                                {/* Barre de couleur */}
                                <div style={{
                                    height: '6px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                }} />

                                <div style={{ padding: '24px' }}>
                                    {/* En-tête */}
                                    <div style={{
                                        textAlign: 'center',
                                        marginBottom: '20px'
                                    }}>
                                        <Avatar
                                            size={80}
                                            style={{
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                marginBottom: '12px',
                                                fontSize: '32px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {item?.prenom?.[0] || ""}{item?.nom?.[0] || ""}
                                        </Avatar>
                                        <h3 style={{
                                            fontSize: '18px',
                                            fontWeight: '700',
                                            color: '#1e293b',
                                            margin: '0 0 6px 0'
                                        }}>
                                            {item?.prenom ? `${item?.nom} ${item?.prenom}` : item?.nom}
                                        </h3>
                                        <Tag
                                            style={{
                                                fontSize: '12px',
                                                fontWeight: '600',
                                                padding: '4px 12px',
                                                borderRadius: '8px',
                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                color: 'white',
                                                border: 'none'
                                            }}
                                        >
                                            Expert N° {index + 1}
                                        </Tag>
                                    </div>

                                    {/* Spécialité */}
                                    {item.specialite && (
                                        <div style={{
                                            background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)',
                                            borderRadius: '12px',
                                            padding: '12px',
                                            marginBottom: '16px',
                                            border: '2px solid #6366f1',
                                            textAlign: 'center'
                                        }}>
                                            <div style={{
                                                fontSize: '14px',
                                                fontWeight: '700',
                                                color: '#4338ca'
                                            }}>
                                                {item.specialite}
                                            </div>
                                        </div>
                                    )}

                                    {/* Profession */}
                                    <div style={{ marginBottom: '16px' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            marginBottom: '8px'
                                        }}>
                                            <div style={{
                                                width: '4px',
                                                height: '16px',
                                                borderRadius: '2px',
                                                background: 'linear-gradient(180deg, #3b82f6 0%, #2563eb 100%)'
                                            }} />
                                            <span style={{
                                                fontSize: '12px',
                                                color: '#64748b',
                                                fontWeight: '700',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}>
                                                Profession
                                            </span>
                                        </div>
                                        <div style={{
                                            fontSize: '14px',
                                            fontWeight: '600',
                                            color: '#334155',
                                            paddingLeft: '12px'
                                        }}>
                                            {item?.profession || "-"}
                                        </div>
                                    </div>

                                    {/* Coordonnées */}
                                    <div style={{
                                        background: 'linear-gradient(135dg, #e0f2fe 0%, #bae6fd 100%)',
                                        borderRadius: '12px',
                                        padding: '14px',
                                        marginBottom: '16px',
                                        border: '1px solid #7dd3fc'
                                    }}>
                                        {item.contact && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px',
                                                marginBottom: item.email ? '10px' : '0'
                                            }}>
                                                <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '8px',
                                                    background: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}>
                                                    <PhoneOutlined style={{ color: '#0284c7', fontSize: '16px' }} />
                                                </div>
                                                <span style={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#0c4a6e',
                                                    flex: 1
                                                }}>
                                                    {item.contact ? item.contact[0] : item.telephone}
                                                </span>
                                            </div>
                                        )}
                                        {item.email && (
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '8px',
                                                    background: 'white',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }}>
                                                    <MailOutlined style={{ color: '#0284c7', fontSize: '16px' }} />
                                                </div>
                                                <span style={{
                                                    fontSize: '13px',
                                                    fontWeight: '500',
                                                    color: '#0c4a6e',
                                                    flex: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {item.email}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Adresse */}
                                    {item.adresse && (
                                        <div style={{ marginBottom: '16px' }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                gap: '10px',
                                                fontSize: '13px',
                                                color: '#64748b'
                                            }}>
                                                <EnvironmentOutlined style={{
                                                    color: '#a855f7',
                                                    fontSize: '16px',
                                                    marginTop: '2px'
                                                }} />
                                                <span style={{ fontWeight: '500', lineHeight: '1.5' }}>
                                                    {item.adresse}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    {/* Date d'enregistrement */}
                                    <div style={{
                                        borderTop: '1px dashed #e2e8f0',
                                        paddingTop: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontSize: '12px',
                                        color: '#94a3b8'
                                    }}>
                                        <CalendarOutlined />
                                        <span>Enregistré le {frenchDate(item?.createdAt)}</span>
                                    </div>
                                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            danger
                                            type="primary"
                                            icon={<CloseCircleOutlined />}
                                            onClick={() => confirmCancelExpert(item)}
                                            loading={cancelLoadingId === item.codeId}
                                            disabled={sinistre?.sortDossier === "S" || sinistre?.sortDossier === "T"}
                                        >
                                            Retirer l'expert
                                        </Button>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    ))}
                </Row>
            ) : (
                <Card style={{
                    borderRadius: '16px',
                    border: 'none',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    padding: '40px'
                }}>
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <div>
                                <p style={{
                                    fontSize: '16px',
                                    color: '#64748b',
                                    fontWeight: '500',
                                    margin: '16px 0 8px 0'
                                }}>
                                    Aucun expert enregistré
                                </p>
                                <p style={{
                                    fontSize: '14px',
                                    color: '#94a3b8',
                                    margin: 0
                                }}>
                                    Cliquez sur le bouton ci-dessus pour affecter des experts au dossier
                                </p>
                            </div>
                        }
                    />
                </Card>
            )}
        </div>
    );


    const items = [
        {
            key: "expertises",
            label: (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '15px',
                    fontWeight: '600',
                    marginLeft: '15px'
                }}>
                    <FileTextOutlined />
                    <span>Expertises</span>
                    <Badge
                        count={expertises.length}
                        showZero
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}
                    />
                </div>
            ),
            children: expertiseTabContent,
        },
        {
            key: "experts",
            label: (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '15px',
                    fontWeight: '600'
                }}>
                    <TeamOutlined />
                    <span>Experts</span>
                    <Badge
                        count={experts.length}
                        showZero
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}
                    />
                </div>
            ),
            children: expertsTabContent,
        },
    ];

    return (
        <div
            style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 100%)',
                minHeight: '100vh',
                padding: '24px'
            }}
        >
            <div style={{ maxWidth: '1600px', margin: '0 auto' }}>
                {/* En-tête */}
                <div
                    style={{
                        background: 'white',
                        borderRadius: '16px',
                        padding: '8px 24px',
                        marginBottom: '24px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '12px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
                        }}>
                            <SafetyOutlined style={{ fontSize: '28px', color: 'white' }} />
                        </div>
                        <div>
                            <h2 style={{
                                fontSize: '24px',
                                fontWeight: '700',
                                color: '#1e293b',
                                margin: '0 0 4px 0'
                            }}>
                                Gestion des Experts
                            </h2>
                            <p style={{
                                fontSize: '14px',
                                color: '#64748b',
                                margin: 0
                            }}>
                                Affectation et suivi des expertises du sinistre
                            </p>
                        </div>
                    </div>
                </div>

                {/* Contenu avec onglets */}
                <Card
                    style={{
                        borderRadius: '16px',
                        border: 'none',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        overflow: 'hidden'
                    }}
                    styles={{ body: { padding: 0 } }}
                >
                    <Tabs
                        items={items}
                        size="large"
                        style={{
                            padding: '0'
                        }}
                    />
                </Card>
            </div>
            <AjouterExpertise
                open={isExpertiseVisible}
                modalExpert={isExpertiseVisible}
                expertise={currentExpertise}
                setOpen={setIsExpertiseVisible}
                sinistre={sinistre}
                edit={editMode}
            />
            <AssignExpertsModal
                open={isAssignExpertVisible}
                sinistre={sinistre}
                assignedExperts={experts}
                onClose={() => setIsAssignExpertVisible(false)}
                onSuccess={() => {
                    setIsAssignExpertVisible(false);
                    setRefetch && setRefetch((prev) => !prev);
                }}
            />
            
            {/* Modal Note Expert */}
            <Modal
                open={letterModal}
                onCancel={() => setLetterModal(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setLetterModal(false)}>Fermer</Button>,
                    <Button
                        key="print"
                        onClick={handlePrint}
                        type="primary"
                        style={{ background: "green" }}
                        icon={<PrinterOutlined />}
                    >
                        Imprimer
                    </Button>
                ]}
                title="Note à l'Expert"
                destroyOnClose
                zIndex={2000}
            >
                <div style={{ background: 'white', padding: 16 }}>
                    <NoteExpert ref={printRef} expertise={currentExpertise} sinistre={sinistre} />
                </div>
            </Modal>

            {/* Modal Note Victime */}
            <Modal
                open={victimLetterModal}
                onCancel={() => setVictimLetterModal(false)}
                width={800}
                footer={[
                    <Button key="cancel" onClick={() => setVictimLetterModal(false)}>Fermer</Button>,
                    <Button
                        key="print"
                        onClick={handleVictimPrint}
                        type="primary"
                        style={{ background: "#8b5cf6" }}
                        icon={<PrinterOutlined />}
                    >
                        Imprimer
                    </Button>
                ]}
                title="Note à la Victime"
                destroyOnClose
                zIndex={2000}
            >
                <div style={{ background: 'white', padding: 16 }}>
                    <NoteVictime ref={victimPrintRef} expertise={currentExpertise} sinistre={sinistre} />
                </div>
            </Modal>
        </div>
    );
};

export default ExpertsTab;